import { defineStore } from 'pinia'
import { ref } from 'vue'
import { type Call, CallApi, type CreateCallDto, type UpdateCallDto } from '@/api-swagger'
import { useSuiteStore } from './useSuiteStore'
import { withApiState } from '@/utils/withApiState'

export const useCallStore = defineStore('call', () => {
  const calls = ref<Call[]>([])
  const loading = ref(false)
  const loadingCreate = ref(false)
  const loadingUpdate = ref(false)
  const api = new CallApi()
  const suiteStore = useSuiteStore()

  const getCallsByPartnerId = async (partnerId: number) => {
    return withApiState(
      async () => {
        calls.value = []
        const response = await suiteStore.suiteFetch(`call/partner/${partnerId}`, { method: 'GET' })
        if (response && response.ok) {
          calls.value = (await response.json()) || []
        } else {
          calls.value = (await api.getCallsByPartnerId({ partnerId: partnerId })) || []
        }
        calls.value.sort((a, b) => parseDate(b.callDate) - parseDate(a.callDate))
        return calls.value
      },
      { loading },
    )
  }

  const createCall = async (call: CreateCallDto) => {
    return withApiState(
      async () => {
        const suiteResponse = await suiteStore.suiteFetch('call', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(call),
        })
        if (suiteResponse && suiteResponse.ok) {
          return await suiteResponse.json()
        }
        await api.createCall({ createCallDto: call })
      },
      { loading: loadingCreate, rethrow: true },
    )
  }

  const parseDate = (dateStr: string) => {
    const [year, month, day, hour, minute] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day, hour, minute).getTime()
  }

  const updateCall = async (callId: number, updatedCall: UpdateCallDto) => {
    return withApiState(
      async () => {
        let modified = false
        const suiteResponse = await suiteStore.suiteFetch(`call/${callId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedCall),
        })
        if (suiteResponse && suiteResponse.ok) {
          const result = await suiteResponse.json()
          modified = result?.modified ?? true
        } else {
          const response = await api.updateCallComment({ id: callId, updateCallDto: updatedCall })
          modified = response.modified ?? false
        }
        if (modified) {
          const index = calls.value.findIndex((call) => call.id === callId)
          if (index !== -1) {
            calls.value[index] = { ...calls.value[index], ...updatedCall }
          }
        }
        return modified
      },
      { loading: loadingUpdate },
    )
  }
  return {
    calls,
    loading,
    loadingCreate,
    loadingUpdate,
    updateCall,
    getCallsByPartnerId,
    createCall,
  }
})
